import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useParentShell } from './ParentShell';

/**
 * Главная страница ЛК родителя.
 *
 * Всё, что рендерится, — ЖИВЫЕ данные из `/api/parent/dashboard`
 * (KPI задолженности, средний балл, «Сегодня · уроки», счёт к оплате
 * в «Что нового»). Мок-блоки (заметка классного руководителя и фейковая
 * лента) удалены: они показывали всем родителям одни и те же чужие
 * данные. Настоящая лента (оценки, ДЗ, комментарии учителя) — будущая
 * задача: агрегирующий endpoint /api/parent/feed.
 */
export default function ParentHomePage() {
  const { child, activeYear, setHasDebt } = useParentShell();

  // Финансовый KPI с бэка.
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (!child || !activeYear) return;
    let cancelled = false;
    setLoad(true); setError(null);
    api.get(`/api/parent/dashboard?student_id=${child.id}&year_id=${activeYear.id}`)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoad(false); });
    return () => { cancelled = true; };
  }, [child?.id, activeYear?.id]);

  // Сообщаем Shell, есть ли долг — для бейджа на пункте «Оплата».
  useEffect(() => {
    if (!data || !setHasDebt) return;
    setHasDebt(!!(data.finance && data.finance.debt > 0));
  }, [data, setHasDebt]);

  const finance = data?.finance || null;
  const average = data?.average || null;
  const todaySchedule = data?.todaySchedule || null;
  const isDebt  = !!(finance && finance.debt > 0);

  return (
    <div className="pc-body">
      {/* KPI: задолженность по году + средний балл (у детсадовцев балла нет) */}
      <div
        className="pc-hero"
        style={{ gridTemplateColumns: activeYear?.isKg ? '1fr' : 'repeat(2, 1fr)' }}
      >
        <PaymentKpi loading={loading} error={error} finance={finance} />
        {!activeYear?.isKg && (
          <AverageKpi loading={loading} error={error} average={average} />
        )}
      </div>

      {/* Сегодня — расписание уроков. У детсадовцев расписания нет,
          поэтому карточку прячем. Для архивных лет API отдаёт null —
          тоже прячем (для прошлых лет «сегодня» бессмысленно). */}
      {!activeYear?.isKg && todaySchedule && (
        <TodayScheduleCard schedule={todaySchedule} />
      )}

      {/* Лента «Что нового» — показываем только ЖИВЫЕ события (сейчас это
          счёт к оплате из /api/parent/dashboard). Мок-заметка классного
          руководителя и фейковые события («оценка», «ДЗ», «собрание»)
          удалены — они были одинаковыми у всех родителей и путали людей.
          Настоящая лента (оценки, ДЗ, комментарии учителя) — отдельной
          задачей через /api/parent/feed. */}
      {isDebt && (
        <div className="pc-card">
          <div className="pc-card__head">
            <div className="pc-card__title">Что нового</div>
          </div>
          <div className="pc-card__body">
            <div className="pc-feed">
              <FeedItem
                icon="wallet"
                modifier="pay"
                title={<><b>Счёт за месяц — {fmtRub(finance.debt)}</b></>}
                sub={finance.nextDue?.daysOverdue > 0
                  ? `Просрочен на ${finance.nextDue.daysOverdue} дн.`
                  : 'Ожидает оплаты'}
                time={finance.nextDue?.dueDate ? formatDateShort(finance.nextDue.dueDate) : '—'}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Карточка KPI «Задолженность / Всё оплачено». Цвет (красный или зелёный)
 * выбирается через CSS-модификатор. Пока грузим — показываем скелетон.
 */
function PaymentKpi({ loading, error, finance }) {
  if (loading) {
    return (
      <div className="pc-stat">
        <div className="pc-stat__label">Оплата за год</div>
        <div className="pc-stat__val" style={{ color: 'var(--pc-fg-faint)' }}>…</div>
        <div className="pc-stat__hint">загружаем</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="pc-stat">
        <div className="pc-stat__label">Оплата за год</div>
        <div className="pc-stat__val" style={{ fontSize: 14, color: 'var(--pc-warn)' }}>ошибка</div>
        <div className="pc-stat__hint">{error}</div>
      </div>
    );
  }
  if (!finance) {
    return (
      <div className="pc-stat">
        <div className="pc-stat__label">Оплата за год</div>
        <div className="pc-stat__val" style={{ color: 'var(--pc-fg-faint)' }}>—</div>
        <div className="pc-stat__hint">нет данных</div>
      </div>
    );
  }
  const isDebt = finance.debt > 0;
  return (
    <div className={'pc-stat ' + (isDebt ? 'pc-stat--debt' : 'pc-stat--paid')}>
      <div className="pc-stat__label">{isDebt ? 'Задолженность' : 'Оплачено'}</div>
      <div className="pc-stat__val">{isDebt ? fmtRub(finance.debt) : '✓'}</div>
      <div className="pc-stat__hint">
        {isDebt
          ? hintFromNextDue(finance.nextDue)
          : 'Все счета оплачены'}
      </div>
    </div>
  );
}

/**
 * KPI «Среднее за <триместр>».
 *
 *   • Период (I/II/III триместр) приходит с бэка — он считается
 *     по `years_study.trimester_dates` и сегодняшней дате.
 *   • Для 0-2 классов (безотметочное обучение) бэк отдаёт
 *     `hasGrades=false`, и тогда вместо балла показываем
 *     «— безотметочный».
 *   • «Подробнее →» прижата вправо к строке-подсказке и ведёт
 *     в дневник — там полная разбивка по предметам/триместрам.
 */
function AverageKpi({ loading, error, average }) {
  if (loading) {
    return (
      <div className="pc-stat">
        <div className="pc-stat__label">Среднее за триместр</div>
        <div className="pc-stat__val" style={{ color: 'var(--pc-fg-faint)' }}>…</div>
        <div className="pc-stat__hint">загружаем</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="pc-stat">
        <div className="pc-stat__label">Среднее за триместр</div>
        <div className="pc-stat__val" style={{ fontSize: 14, color: 'var(--pc-warn)' }}>ошибка</div>
        <div className="pc-stat__hint">{error}</div>
      </div>
    );
  }
  if (!average) {
    return (
      <div className="pc-stat">
        <div className="pc-stat__label">Среднее за триместр</div>
        <div className="pc-stat__val" style={{ color: 'var(--pc-fg-faint)' }}>—</div>
        <div className="pc-stat__hint">нет данных</div>
      </div>
    );
  }
  const { periodLabel, hasGrades, avg, subjectsCount } = average;
  return (
    <div className="pc-stat">
      <div className="pc-stat__label">Среднее за {periodLabel}</div>
      <div className="pc-stat__val">
        {hasGrades
          ? (avg != null
              ? avg.toFixed(1)
              : <span style={{ color: 'var(--pc-fg-faint)' }}>—</span>)
          : <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--pc-fg-faint)' }}>безотметочный</span>}
      </div>
      <div className="pc-stat__hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          {hasGrades
            ? (subjectsCount > 0 ? `по ${subjectsCount} ${pluralSubject(subjectsCount)}` : 'оценок пока нет')
            : 'Только словесная оценка'}
        </span>
        {hasGrades && (
          <Link to="/parent/journal" className="pc-stat__more">Подробнее →</Link>
        )}
      </div>
    </div>
  );
}

/**
 * «Сегодня · вторник, 7 мая» — карточка расписания на сегодня.
 *
 * Бэк отдаёт уже сформированную метку «N мая», название дня недели и
 * упорядоченный список уроков с признаком `isNow` для текущего урока.
 * Если уроков нет — печатаем «нет занятий» одной строкой, без сетки
 * пустых ячеек.
 *
 * «Расписание недели →» — обычный Link на /parent/schedule.
 */
function TodayScheduleCard({ schedule }) {
  const lessons = schedule.lessons || [];
  const count = lessons.length;
  const holiday = schedule.holiday || null; // {kind, label}
  const sub = holiday
    ? (holiday.kind === 'vacation' ? 'каникулы' : 'выходной')
    : (count > 0 ? `${count} ${pluralLessons(count)}` : 'нет занятий')
      + (schedule.transferredFromLabel ? ` · по расписанию ${schedule.transferredFromLabel}` : '');
  return (
    <div className="pc-card">
      <div className="pc-card__head">
        <div className="pc-card__title">
          Сегодня · {schedule.weekday}, {schedule.dateLabel}
        </div>
        <span className="pc-card__sub">{sub}</span>
        <Link to="/parent/schedule" className="pc-card__action">Расписание недели →</Link>
      </div>
      <div className="pc-card__body">
        {holiday ? (
          <div style={{ color: 'var(--pc-fg-faint)', padding: '12px 0' }}>
            {holiday.kind === 'vacation' ? 'Каникулы' : 'Выходной'}
            {holiday.label ? <>: {holiday.label}</> : null}. Уроков нет.
          </div>
        ) : count === 0 ? (
          <div style={{ color: 'var(--pc-fg-faint)', padding: '12px 0' }}>
            Сегодня уроков нет. Можно отдохнуть.
          </div>
        ) : (
          <div className="pc-today">
            {lessons.map((l) => (
              <div
                key={l.position}
                className={'pc-today-item' + (l.isNow ? ' is-now' : '')}
              >
                {l.isNow && <span className="pc-today-item__now-tag">сейчас</span>}
                <div className="pc-today-item__time">
                  {l.timeStart && l.timeEnd
                    ? `${l.timeStart} – ${l.timeEnd}`
                    : `№ ${l.position}`}
                </div>
                <div className="pc-today-item__subj">{l.subjectName}</div>
                {l.room && <div className="pc-today-item__room">каб. {l.room}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function pluralLessons(n) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'уроков';
  if (m10 === 1) return 'урок';
  if (m10 >= 2 && m10 <= 4) return 'урока';
  return 'уроков';
}

function pluralSubject(n) {
  // 1 предмет, 2-4 предмета, 5+ предметов
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'предметам';
  if (m10 === 1) return 'предмету';
  if (m10 >= 2 && m10 <= 4) return 'предметам';
  return 'предметам';
}

function hintFromNextDue(nextDue) {
  if (!nextDue) return 'Требует оплаты';
  const due = nextDue.dueDate ? formatDateLong(nextDue.dueDate) : null;
  const overdue = nextDue.daysOverdue > 0 ? ` · просрочка ${nextDue.daysOverdue} дн.` : '';
  return (due ? `до ${due}` : 'требует оплаты') + overdue;
}

function fmtRub(n) {
  const v = Math.round((Number(n) || 0));
  return v.toLocaleString('ru-RU') + ' ₽';
}
function formatDateLong(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const months = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
  return Number(m[3]) + ' ' + months[Number(m[2]) - 1];
}
function formatDateShort(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const months = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
  return Number(m[3]) + ' ' + months[Number(m[2]) - 1];
}

function FeedItem({ icon, modifier, title, sub, time }) {
  return (
    <div className="pc-feed-item">
      <div className={'pc-feed-item__icon pc-feed-item__icon--' + modifier}>
        <Icon name={icon} size={14} />
      </div>
      <div className="pc-feed-item__main">
        <div className="pc-feed-item__title">{title}</div>
        {sub && <div className="pc-feed-item__sub">{sub}</div>}
      </div>
      <span className="pc-feed-item__time">{time}</span>
    </div>
  );
}
